import Typography from '@hellodarwin/core/lib/components/common/typography';
import { ElementTheme } from '@hellodarwin/core/lib/components/common/typography/entities';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import React from 'react';
import CountUp from 'react-countup';
import { useTheme } from 'styled-components';
import { useAppSelector } from '../../app/app-hooks';
import {
  selectFundingExplorerTotalAmount,
  selectFundingExplorerTotalCount,
  selectHasLoadedFundingExplorerResults,
} from '../../features/api/slices/grants-slice';
import { FundingExplorerTotal } from './styles';
import { getCountUpDigit } from './utils/getCountUpDigit';

interface GrantsDirectoryTotalProps {
  elementTheme?: ElementTheme;
  elementThemeMobile?: ElementTheme;
  fontWeight?: number;
}

type DomIsolatedCountUpProps = Omit<
  React.ComponentProps<typeof CountUp>,
  'children' | 'delay'
>;

const DomIsolatedCountUp: React.FC<DomIsolatedCountUpProps> = (props) => (
  <CountUp {...props} delay={0}>
    {/* CountUp owns this node's text so React never reconciles its mutations. */}
    {({ countUpRef }) => (
      <span ref={countUpRef as React.RefObject<HTMLSpanElement>} />
    )}
  </CountUp>
);

const GrantsDirectoryTotal: React.FC<GrantsDirectoryTotalProps> = ({
  elementTheme,
  elementThemeMobile,
  fontWeight,
}) => {
  const { t } = useTranslations();
  const theme = useTheme();

  const totalAmount = useAppSelector(selectFundingExplorerTotalAmount);
  const totalCount = useAppSelector(selectFundingExplorerTotalCount);
  const hasLoadedResults = useAppSelector(
    selectHasLoadedFundingExplorerResults,
  );

  if (!hasLoadedResults) return null;

  return (
    <FundingExplorerTotal>
      {!totalCount ? (
        <Typography
          elementTheme={elementTheme || 'h6'}
          style={{ fontWeight: fontWeight || 400 }}
          nowrap
          inline
        >
          {t(`grantsFundingExplorer|grantsnoGrants`)}
        </Typography>
      ) : (
        <>
          <Typography
            elementTheme={elementTheme || 'h6'}
            nowrap
            style={{ textAlign: 'center' }}
            tablet={{
              elementTheme: elementThemeMobile || 'subtitle1',
              nowrap: false,
            }}
            inline
          >
            <span
              style={{
                color: theme.colors.purple_1,
                display: 'inline-block',
              }}
            >
              <DomIsolatedCountUp
                end={totalCount}
                separator=" "
                duration={1}
              />
            </span>{' '}
            {t(`grants|grants`)}{' '}
            {totalAmount !== undefined &&
              totalAmount !== 0 &&
              t(`grants|grantsTotal.totaling`)}{' '}
            <span
              style={{
                color: theme.colors.purple_1,
                display: 'inline-block',
                minWidth: `${getCountUpDigit(totalAmount)}ch`,
              }}
            >
              {totalAmount > 0 && (
                <DomIsolatedCountUp
                  end={totalAmount}
                  separator={t(
                    `grantsFundingExplorer|grantsgrantsTotalSeperatorTotal`,
                  ).replace(' ', '\u00A0')}
                  prefix={t(
                    `grantsFundingExplorer|grantsgrantsTotalPrefixTotal`,
                  )}
                  suffix={t(
                    `grantsFundingExplorer|grantsgrantsTotalSuffixTotal`,
                  )}
                  duration={1}
                />
              )}
            </span>
          </Typography>
        </>
      )}
    </FundingExplorerTotal>
  );
};

export default GrantsDirectoryTotal;
